import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors, breakpoints, fadeIn } from '../../../assets/styles/GlobalStyles';
import { userRequestsService } from '../services/userRequestsService';
import { FaArrowLeft } from 'react-icons/fa';
import MainLayout from '../../../layouts/MainLayout';
import RequestCard from '../components/RequestCard';
import RequestsSkeleton from '../components/RequestsSkeleton';
import RequestsError from '../components/RequestsError';
import EmptyRequests from '../components/EmptyRequests';
import { useAuth } from '../../../contexts/AuthContext';
import { cancelarRequerimento, isRascunhoAberto, isAguardandoPagamento } from '../../requerimentos/core/services/protocoloRequerimentoService';
import { buscarCobrancaPorRequerimento } from '../../requerimentos/core/services/tituloService';
import { getPessoaIdRascunho, obterRascunhoPorRequerimento, removerRascunhoPorRequerimento } from '../../requerimentos/core/services/rascunhoPedidoService';

const PageContainer = styled.div`
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 300px;
    background: linear-gradient(135deg, ${colors.blueDark} 0%, ${colors.blueMedium} 100%);
    opacity: 0.05;
    pointer-events: none;
    z-index: 0;
  }
`;

const RequestsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 120px 2rem 2rem 2rem;
  animation: ${fadeIn} 0.5s ease-out;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 100px 1.5rem 1.5rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 90px 1rem 1rem 1rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  color: ${colors.darkBlue};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 1.5rem;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  svg {
    font-size: 1rem;
    transition: transform 0.2s ease;
  }

  &:hover {
    color: ${colors.lightBlue};
    
    svg {
      transform: translateX(-3px);
    }
  }
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a2c3e;
  margin-bottom: 8px;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.4rem;
  }
`;

const PageSubtitle = styled.p`
  color: #5a6e7c;
  margin-bottom: 32px;
  font-size: 0.9rem;
`;

const RequestsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RequestsCount = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
  font-size: 0.85rem;
  color: #5a6e7c;
  
  strong {
    color: ${colors.darkBlue};
    margin-right: 4px;
  }
`;

const useRequests = () => {
  const [requests, setRequests] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadRequests = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await userRequestsService.getRequests();
      
      if (response.success) {
        setRequests(response.data);
      } else {
        setError(response.error || 'Erro ao carregar solicitações');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  return { requests, loading, error, loadRequests };
};

const RequestsPage = () => {
  const navigate = useNavigate();
  const { requests, loading, error, loadRequests } = useRequests();
  const { user } = useAuth();
  const pessoaId = getPessoaIdRascunho(user);

  const handleGoBack = () => navigate('/home');

  const handleContinuar = (rascunho) => {
    navigate(`/requerimentos/${rascunho.departamentoId}/${rascunho.pedidoId}?continuar=1`);
  };

  // Erro sobe pro card, que mostra a mensagem e deixa tentar de novo.
  const handleDescartar = async (request) => {
    await cancelarRequerimento(request.requerimentoId);
    removerRascunhoPorRequerimento(pessoaId, request.requerimentoId);
    await loadRequests();
  };

  // Erro sobe pro card, que mostra a mensagem e deixa tentar de novo.
  const handleBuscarCobranca = (request) =>
    buscarCobrancaPorRequerimento({ requerimentoId: request.requerimentoId, user });

  // Estado de carregamento: mostra skeleton
  if (loading) {
    return (
      <MainLayout>
        <PageContainer>
          <RequestsContainer>
            <BackButton onClick={handleGoBack}>
              <FaArrowLeft /> Voltar para Home
            </BackButton>
            <PageTitle>Minhas Solicitações</PageTitle>
            <PageSubtitle>Visualize todos os seus requerimentos e pedidos</PageSubtitle>
            <RequestsSkeleton />
          </RequestsContainer>
        </PageContainer>
      </MainLayout>
    );
  }

  // Estado de erro: mostra mensagem e botão de tentar novamente
  if (error) {
    return (
      <MainLayout>
        <PageContainer>
          <RequestsContainer>
            <BackButton onClick={handleGoBack}>
              <FaArrowLeft /> Voltar para Home
            </BackButton>
            <PageTitle>Minhas Solicitações</PageTitle>
            <PageSubtitle>Visualize todos os seus requerimentos e pedidos</PageSubtitle>
            <RequestsError error={error} onRetry={loadRequests} />
          </RequestsContainer>
        </PageContainer>
      </MainLayout>
    );
  }

  const hasRequests = requests && requests.length > 0;

  // Estado com dados: mostra a lista de solicitações
  return (
    <MainLayout>
      <PageContainer>
        <RequestsContainer>
          <BackButton onClick={handleGoBack}>
            <FaArrowLeft /> Voltar para Home
          </BackButton>
          
          <PageTitle>Minhas Solicitações</PageTitle>
          <PageSubtitle>Visualize todos os seus requerimentos e pedidos</PageSubtitle>
          
          {/* Contador de solicitações (apenas se houver itens) */}
          {hasRequests && (
            <RequestsCount>
              <strong>{requests.length}</strong> solicitação(ões) encontrada(s)
            </RequestsCount>
          )}
          
          {/* Lista de cards ou mensagem vazia */}
          <RequestsList>
            {hasRequests ? (
              requests.map((request, index) => {
                const isRascunho = Boolean(request.requerimentoId) && isRascunhoAberto(request.situacaoCodigo, request.documentoId);
                return (
                  <RequestCard
                    key={`${request.solicitacaoServico}-${index}`}
                    request={request}
                    isRascunho={isRascunho}
                    isAguardandoPagamento={Boolean(request.requerimentoId) && isAguardandoPagamento(request.situacaoCodigo)}
                    onBuscarCobranca={handleBuscarCobranca}
                    rascunhoLocal={isRascunho ? obterRascunhoPorRequerimento(pessoaId, request.requerimentoId) : null}
                    onContinuar={handleContinuar}
                    onDescartar={handleDescartar}
                  />
                );
              })
            ) : (
              <EmptyRequests />
            )}
          </RequestsList>
        </RequestsContainer>
      </PageContainer>
    </MainLayout>
  );
};

export default RequestsPage;